/**
 * window.__dw — the single control surface for the debug panel, Playwright tests and the
 * screenshot harness. Anything a human can trigger from the debug UI must be reachable
 * here, so tests and humans can never diverge (IMPLEMENTATION_PLAN.md H4).
 */

import type { RenderWorld } from '../render/world';
import { DECK_HEIGHT, EYE_Z } from '../render/constants';
import {
  WEATHER_PRESETS,
  cloneWeather,
  type WeatherPresetName,
  type WeatherState,
} from '../sim/weather';
import type { VesselKind } from '../sim/types';

export interface PerfSnapshot {
  fps: number;
  frameTimeMs: number;
  drawCalls: number;
  triangles: number;
  vertices: number;
  jsHeapMb: number | null;
  quality: string;
  /** Clock last handed to the ocean/sky shaders. If this stops advancing, the sea is frozen. */
  oceanTime: number;
}

export interface DebugApi {
  /** True once the first frame has rendered and the scene is ready to capture. */
  ready: boolean;
  version: string;

  /** Freeze the clock so screenshots are deterministic. */
  freeze(): void;
  unfreeze(): void;
  isFrozen(): boolean;
  /** Set absolute simulation time, in seconds. Implies freeze. */
  setTime(seconds: number): void;
  getTime(): number;
  /** Advance the frozen clock by n fixed 1/60s ticks. */
  stepTicks(n: number): void;

  setWeather(preset: WeatherPresetName): void;
  getWeather(): WeatherState;
  setSeaState(v: number): void;
  setTimeOfDay(hours: number): void;
  setFog(v: number): void;

  /** Point the wide camera: yaw/pitch in degrees, optional vertical FOV in degrees. */
  setCamera(yawDeg: number, pitchDeg: number, fovDeg?: number): void;
  /**
   * Fly the camera off its deck mount, in metres.
   *
   * The camera is normally bolted to the observation platform because that is the game's
   * only viewpoint. This exists for inspection — an aerial of the rig, a look at a
   * structure from outside, checking what a silhouette does from an angle no player will
   * ever have. Never used by the game itself; `resetCameraPosition` puts it back.
   */
  setCameraPosition(x: number, y: number, z: number): void;
  resetCameraPosition(): void;

  perf(): PerfSnapshot;
  /** Resolves after `n` rendered frames — lets the harness flush before capture. */
  waitFrames(n: number): Promise<void>;
  /**
   * Replay a hull strike after shader warm-up, so sub-second FX can be captured rather
   * than expiring while the benchmark page is still becoming ready.
   */
  triggerHullImpact(
    x: number, y: number, z: number, ricochet?: boolean, deflectSign?: -1 | 1,
  ): void;
  /** Replay an enemy launch flash after shader warm-up for deterministic capture. */
  triggerHostileMuzzleFlash(
    x: number,
    y: number,
    z: number,
    dirX: number,
    dirY: number,
    dirZ: number,
    kind: VesselKind,
    sourceHeading: number,
  ): void;

  // --- Gameplay. Present only once a Game is running (absent in bench-only boots). ---

  /** A plain, serialisable read of the simulation — what tests assert against. */
  state?(): GameStateSnapshot;
  /** Spawn a vessel at a bearing (degrees) and range (metres). */
  spawn?(kind: string, bearingDeg: number, rangeMetres: number): number;
  /**
   * Empty a hull, so the simulation kills it on the next tick. Returns false if there
   * is no such vessel.
   *
   * Deliberately sets HP rather than the state: `killIfDead` is swept over every vessel
   * each tick precisely so a hull at zero sinks no matter which path emptied it, and a
   * debug surface that assigned `'sinking'` directly would skip the wreck event, the
   * critical-detonation roll and the score. Landing a shell on a chosen hull from a test
   * is otherwise a matter of solving the gun's own ballistics, which tests what the
   * ballistics do, not what the death does.
   */
  sink?(id: number): boolean;
  /** Select a contact by id, or clear with null. */
  select?(id: number | null): void;
  /** Switch view. */
  setView?(view: 'horizon' | 'scope'): void;
  /** Set scope magnification, 2..8. */
  setZoom?(magnification: number): void;
  /** Hold or release the trigger. */
  setFiring?(on: boolean): void;
  /** Give the player everything, for FX and feel testing. */
  refill?(): void;
  /** Aim the searchlight: bearing in degrees and depression in degrees, or null to douse it. */
  setSpotlight?(bearingDeg: number | null, depressionDeg?: number): void;
  /** Point the view: yaw and pitch in degrees, through the same look controls as the mouse. */
  setLook?(yawDeg: number, pitchDeg: number): void;
  /** Start a specific shift immediately. */
  startShift?(index: number): void;
  /**
   * Which bearings the rig's own structure masks, sampled across the free-look arc.
   *
   * A design instrument, not a toy: friendly vessels approach on scripted bearings, and a
   * bearing that the derrick or the process modules sit in front of is one the player
   * cannot engage along. Guessing that sector from screenshots is how you get a convoy
   * arriving behind a crane.
   */
  maskedBearings?(pitchDeg?: number): Array<{ from: number; to: number }>;
}

/** Serialisable projection of simulation state for tests and tooling. */
export interface GameStateSnapshot {
  time: number;
  shift: { index: number; elapsed: number; objective: string; outcome: string;
    arrivalsSafe: number; arrivalsLost: number; hostilesDestroyed: number;
    roundsFired: number; roundsHit: number };
  resources: { money: number; oil: number; integrity: number; crew: number; ammo: number };
  vessels: Array<{ id: number; kind: string; faction: string; state: string;
    hp: number; range: number; detection: string; y: number }>;
  projectiles: number;
  selectedId: number | null;
  view: string;
  magnification: number;
  /** Where the player is looking, degrees. Positive yaw is right, positive pitch is down. */
  cameraYawDeg: number;
  cameraPitchDeg: number;
  /** Vertical aim inversion currently in force for the active view. */
  inverted: boolean;
  /**
   * Weapon state, so a harness can tell "the trigger did nothing" apart from "the tube
   * was empty" and "the interlock was still running" — three failures that look
   * identical from outside and need completely different fixes.
   */
  /**
   * The autocannon's aim-lock — the crosshair dwell that puts the lead pip up. Exposed
   * because "the pip did not appear" and "the pip appeared on the wrong hull" are
   * different bugs, and a harness cannot tell them apart from the DOM alone.
   */
  gunLock: { targetId: number | null; progress: number; locked: boolean };
  weapons: {
    selected: string;
    torpedoes: number;
    torpedoTubes: number;
    torpedoInterlock: number;
    depthCharges: number;
    fuseDepth: number;
  };
}

declare global {
  interface Window {
    __dw?: DebugApi;
    /** The boot bar's target-setter, installed inline by index.html before any module. */
    __boot?: (pct: number, label?: string) => void;
    /** Set by the boot bar once it has visually reached 100%. */
    __bootSettled?: boolean;
  }
}

export interface DebugHost {
  world: RenderWorld;
  freeze: () => void;
  unfreeze: () => void;
  isFrozen: () => boolean;
  setTime: (t: number) => void;
  stepTicks: (n: number) => void;
  onFrame: (cb: () => void) => () => void;
}

export function installDebugApi(host: DebugHost): DebugApi {
  const { world } = host;

  const api: DebugApi = {
    ready: false,
    version: '0.1.0',

    freeze: () => host.freeze(),
    unfreeze: () => host.unfreeze(),
    isFrozen: () => host.isFrozen(),
    setTime: (s: number) => host.setTime(s),
    getTime: () => world.time,
    stepTicks: (n: number) => host.stepTicks(n),

    setWeather(preset: WeatherPresetName) {
      world.setWeather(WEATHER_PRESETS[preset]);
    },
    getWeather: () => cloneWeather(world.currentWeather),
    setSeaState(v: number) {
      world.setWeather({ ...world.currentWeather, seaState: v });
    },
    setTimeOfDay(hours: number) {
      world.setWeather({ ...world.currentWeather, timeOfDay: hours }, false);
    },
    setFog(v: number) {
      world.setWeather({ ...world.currentWeather, fogDensity: v }, false);
    },

    setCamera(yawDeg: number, pitchDeg: number, fovDeg?: number) {
      const yaw = (yawDeg * Math.PI) / 180;
      const pitch = (pitchDeg * Math.PI) / 180;
      world.camera.rotation.set(pitch, yaw, 0);
      if (fovDeg !== undefined) world.camera.fov = (fovDeg * Math.PI) / 180;
    },

    setCameraPosition(x: number, y: number, z: number) {
      world.camera.position.set(x, y, z);
    },
    resetCameraPosition() {
      world.camera.position.set(0, DECK_HEIGHT, EYE_Z);
    },

    perf(): PerfSnapshot {
      const engine = world.engine;
      const perfMemory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
      return {
        fps: Math.round(engine.getFps() * 10) / 10,
        frameTimeMs: Math.round(world.instrumentation.frameTimeCounter.lastSecAverage * 100) / 100,
        // Per-frame, thanks to SceneInstrumentation resetting the counter each frame.
        drawCalls: world.instrumentation.drawCallsCounter.current,
        triangles: world.currentTriangleCount,
        vertices: world.stats.vertexCount,
        jsHeapMb: perfMemory ? Math.round(perfMemory.usedJSHeapSize / 1048576) : null,
        quality: world.qualityTier,
        oceanTime: Math.round(world.lastPushedTime * 1000) / 1000,
      };
    },

    waitFrames(n: number): Promise<void> {
      return new Promise((resolve) => {
        let left = Math.max(1, n);
        const off = host.onFrame(() => {
          left -= 1;
          if (left <= 0) {
            off();
            resolve();
          }
        });
      });
    },

    triggerHullImpact(x, y, z, ricochet = false, deflectSign = 1) {
      world.effects.hullImpact(x, y, z, ricochet, deflectSign);
    },

    triggerHostileMuzzleFlash(x, y, z, dirX, dirY, dirZ, kind, sourceHeading) {
      world.effects.hostileMuzzleFlash(
        x, y, z, dirX, dirY, dirZ, kind, sourceHeading,
      );
    },
  };

  /*
   * Raw scene handle, debug-only.
   *
   * Added because "find the six lights hanging in mid-air" is unanswerable by reading
   * code — the positions are computed from a dozen constants across several sections, and
   * three attempts at deducing which group they belonged to were all wrong. Enumerating
   * the live scene answers it in one query.
   */
  (api as unknown as { __scene: unknown }).__scene = world.scene;
  window.__dw = api;
  return api;
}

/**
 * Attach the gameplay half of the debug surface.
 *
 * Called by the Game once it exists. Kept separate so a benchmark-only boot — which has
 * no simulation running — still gets the visual half of the API without pretending to
 * have vessels to spawn.
 */
export function installGameDebugApi(api: DebugApi, game: GameDebugHost): void {
  api.state = () => ({
    time: game.sim.time,
    shift: {
      index: game.sim.shift.index,
      elapsed: Math.round(game.sim.shift.elapsed),
      objective: game.sim.shift.objective,
      outcome: game.sim.shift.outcome,
      arrivalsSafe: game.sim.shift.arrivalsSafe,
      arrivalsLost: game.sim.shift.arrivalsLost,
      hostilesDestroyed: game.sim.shift.hostilesDestroyed,
      roundsFired: game.sim.shift.roundsFired,
      roundsHit: game.sim.shift.roundsHit,
    },
    resources: {
      money: Math.round(game.sim.resources.money),
      oil: Math.round(game.sim.resources.oil),
      integrity: Math.round(game.sim.resources.integrity),
      crew: Math.round(game.sim.resources.crew),
      ammo: game.sim.rig.magazine,
    },
    vessels: game.sim.vessels.map((v) => ({
      id: v.id,
      kind: v.kind,
      faction: v.faction,
      state: v.state,
      hp: Math.round(v.hp),
      range: Math.round(Math.hypot(v.pos.x, v.pos.z)),
      // Compass bearing from the rig, so a tool can point the view at a hull without
      // re-deriving the convention (0 = north = +Z, clockwise).
      bearingDeg: Math.round(((Math.atan2(v.pos.x, v.pos.z) * 180) / Math.PI + 360) % 360 * 10) / 10,
      detection: v.detection,
      y: Math.round(v.pos.y * 10) / 10,
    })),
    projectiles: game.sim.projectiles.activeCount,
    selectedId: game.sim.selectedId,
    view: game.view,
    magnification: game.magnification,
    cameraYawDeg: Math.round(game.cameraYawDeg * 100) / 100,
    cameraPitchDeg: Math.round(game.cameraPitchDeg * 100) / 100,
    inverted: game.inverted,
    // Weapon state, so a harness can tell "the trigger did nothing" from "the tube was
    // empty" from "the interlock was still running".
    gunLock: {
      targetId: game.sim.gunLockId,
      progress: Math.round(game.sim.gunLockFraction * 100) / 100,
      locked: game.sim.gunLocked,
    },
    weapons: {
      selected: game.sim.rig.selectedWeapon,
      torpedoes: game.sim.resources.torpedoes,
      torpedoTubes: game.sim.rig.torpedoTubes,
      torpedoInterlock: Math.round(game.sim.rig.torpedoInterlock * 100) / 100,
      depthCharges: game.sim.resources.depthCharges,
      fuseDepth: game.sim.rig.fuseDepth,
    },
  });

  api.spawn = (kind, bearingDeg, rangeMetres) => game.spawnVessel(kind, bearingDeg, rangeMetres);
  api.sink = (id) => {
    const v = game.sim.vessels.find((x) => x.id === id);
    if (!v) return false;
    v.hp = 0;
    return true;
  };
  api.maskedBearings = (pitchDeg) => game.maskedBearings(pitchDeg ?? 1.5);
  api.select = (id) => game.sim.select(id);
  api.setView = (view) => game.setView(view);
  api.setZoom = (m) => game.setMagnification(m);
  api.setFiring = (on) => game.sim.setFiring(on);
  api.refill = () => game.refill();
  api.setSpotlight = (bearingDeg, depressionDeg) => game.aimSpotlight(bearingDeg, depressionDeg);
  api.setLook = (yawDeg, pitchDeg) => game.setLook(yawDeg, pitchDeg);
  api.startShift = (index) => game.startCampaign(index);
}

/** The slice of Game the debug surface needs. Structural, so Game stays uncoupled. */
export interface GameDebugHost {
  sim: {
    time: number;
    shift: { index: number; elapsed: number; objective: string; outcome: string;
      arrivalsSafe: number; arrivalsLost: number; hostilesDestroyed: number;
      roundsFired: number; roundsHit: number };
    resources: { money: number; oil: number; integrity: number; crew: number;
      torpedoes: number; depthCharges: number };
    rig: { magazine: number; selectedWeapon: string; torpedoTubes: number;
      torpedoInterlock: number; fuseDepth: number };
    vessels: ReadonlyArray<{
      id: number; kind: string; faction: string; state: string; hp: number;
      pos: { x: number; y: number; z: number }; detection: string;
    }>;
    projectiles: { activeCount: number };
    selectedId: number | null;
    gunLockId: number | null;
    gunLockFraction: number;
    gunLocked: boolean;
    select: (id: number | null) => void;
    setFiring: (on: boolean) => void;
  };
  view: string;
  magnification: number;
  cameraYawDeg: number;
  cameraPitchDeg: number;
  inverted: boolean;
  setView: (view: 'horizon' | 'scope') => void;
  setMagnification: (m: number) => void;
  spawnVessel: (kind: string, bearingDeg: number, rangeMetres: number) => number;
  maskedBearings: (pitchDeg: number) => Array<{ from: number; to: number }>;
  refill: () => void;
  startCampaign: (index: number) => void;
  aimSpotlight: (bearingDeg: number | null, depressionDeg?: number) => void;
  setLook: (yawDeg: number, pitchDeg: number) => void;
}
